import Button from "@mui/material/Button";
import { omit, pick } from "lodash";
import { useEffect, useRef } from "react";
import TuiImageEditor from "tui-image-editor";
import "tui-image-editor/dist/tui-image-editor.css";
import "./ImageEditor.css";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import { IconButton } from "@mui/material";

interface ImageEditorProps
	extends Omit<React.ComponentProps<"div">, "ref" | "children">,
		tuiImageEditor.IOptions {
	onDone: (e: React.MouseEvent<HTMLButtonElement>, imgStr: string) => void;
	onClose: (e: React.MouseEvent<HTMLElement>) => void;
}

const ImageEditor = ({ onDone, onClose, ...props }: ImageEditorProps) => {
	const divProps = omit(props, [
		"includeUI",
		"cssMaxWidth",
		"cssMaxHeight",
		"usageStatistics",
		"selectionStyle",
	]);
	const editorProps = pick(props, [
		"includeUI",
		"cssMaxWidth",
		"cssMaxHeight",
		"usageStatistics",
		"selectionStyle",
	]);
	const rootEl = useRef<HTMLDivElement | null>(null);
	const imageEditorInst = useRef<TuiImageEditor | null>(null);

	// Bind event handlers
	const bindEventHandlers = (
		currentProps: tuiImageEditor.IOptions,
		prevProps: Record<string, unknown> = {},
	) => {
		if (!imageEditorInst.current) return;
		const keys = Object.keys(editorProps).filter(isEventHandlerKey);
		for (const key of keys) {
			const eventName = key[2].toLowerCase() + key.slice(3);
			// @ts-expect-error - TS doesn't know that the key is an event handler
			if (prevProps[key] !== currentProps[key]) {
				// @ts-expect-error - TS doesn't know that the key is an event handler
				imageEditorInst.current.off(eventName);
				// @ts-expect-error - TS doesn't know that the key is an event handler
				imageEditorInst.current.on(eventName, currentProps[key]);
			}
		}
	};

	// Unbind event handlers
	const unbindEventHandlers = () => {
		if (!imageEditorInst.current) return;
		const keys = Object.keys(editorProps).filter(isEventHandlerKey);
		for (const key of keys) {
			const eventName = key[2].toLowerCase() + key.slice(3);
			// @ts-expect-error - TS doesn't know that the key is an event handler
			imageEditorInst.current.off(eventName);
		}
	};

	const onClickDone = (e: React.MouseEvent<HTMLButtonElement>) => {
		if (!imageEditorInst.current) return;
		const imgStr = imageEditorInst.current.toDataURL();
		return onDone(e, imgStr);
	};

	// Check if the key is an event handler
	const isEventHandlerKey = (key: string) => /on[A-Z][a-zA-Z]+/.test(key);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (!rootEl.current) return () => {};
		imageEditorInst.current = new TuiImageEditor(rootEl.current, editorProps);

		bindEventHandlers(editorProps);

		return () => {
			// Cleanup: unbind events and destroy the instance
			unbindEventHandlers();
			imageEditorInst.current?.destroy();
			imageEditorInst.current = null;
		};
	}, [rootEl.current]);

	useEffect(() => {
		// Bind event handlers when props change
		bindEventHandlers(editorProps);
	});

	return (
		<div className="relative w-full h-full">
			<IconButton
				type="button"
				color="primary"
				className="!absolute top-3 right-3 z-10 !bg-white hover:bg-white/70"
				onClick={onClose}
			>
				<ClearOutlinedIcon className="text-dark" />
			</IconButton>
			<div ref={rootEl} {...divProps} />
			<Button
				type="button"
				variant="contained"
				color="warning"
				className="!absolute bottom-3 right-3 z-10"
				onClick={onClickDone}
			>
				done
			</Button>
		</div>
	);
};

export default ImageEditor;
